/**
 * V2 Basic Config Section Field Configuration
 *
 * V2 equivalent of basicConfigSectionConfig.ts.
 * Maps V2 StepNode.content boolean flags and settings.
 *
 * V2 path changes from V1:
 * - struct.animationEnabled → content.animated
 * - struct.useLocalAssetsIfAvailable → content.offlineFirst
 * - struct.permission.type → content.systemPrompt.permissionType
 * - struct.permission.value → content.systemPrompt.value
 * - struct.timer.timeInterval → content.countdown.duration
 * - struct.timer.timeUnit → content.countdown.unit
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import type { FieldConfig } from '../types/editorFieldTypes';

/**
 * V2 permission type options
 */
export const V2_PERMISSION_TYPE_OPTIONS = [
  { value: 'notification', label: 'Notification' },
  { value: 'att', label: 'ATT (App Tracking Transparency)' },
  { value: 'location', label: 'Location' },
] as const;

/**
 * V2 countdown unit options
 */
export const V2_COUNTDOWN_UNIT_OPTIONS = [
  { value: 'seconds', label: 'Seconds' },
  { value: 'minutes', label: 'Minutes' },
] as const;

/**
 * Animation configs (Screen Entrance, legacy Animation Enabled) moved to
 * v2AnimationSectionConfig.ts — the dedicated Animation section owns them now.
 */

/**
 * V2 Offline First toggle
 *
 * Path: content.offlineFirst
 */
export const V2_OFFLINE_FIRST_CONFIG: FieldConfig = {
  path: 'content.offlineFirst',
  label: 'Offline First',
  type: 'toggle',
  defaultValue: false,
  isNullable: false,
  guideKey: 'screenedit-basic-config-offline-first',
};

/**
 * V2 System Prompt Permission Type
 *
 * Path: content.systemPrompt.permissionType
 */
export const V2_PERMISSION_TYPE_CONFIG: FieldConfig = {
  path: 'content.systemPrompt.permissionType',
  label: 'Permission Type',
  type: 'select',
  options: [...V2_PERMISSION_TYPE_OPTIONS],
  defaultValue: 'notification',
  guideKey: 'screenedit-basic-config-permission-type',
};

/**
 * V2 System Prompt Enabled toggle
 *
 * Path: content.systemPrompt
 */
export const V2_SYSTEM_PROMPT_CONFIG: FieldConfig = {
  path: 'content.systemPrompt',
  label: 'System Permission',
  type: 'toggle',
  defaultValue: null,
  disableBehavior: 'null',
  guideKey: 'screenedit-basic-config-system-permission-enabled',
};

/**
 * V2 Countdown Duration
 *
 * Path: content.countdown.duration
 */
export const V2_COUNTDOWN_DURATION_CONFIG: FieldConfig = {
  path: 'content.countdown.duration',
  label: 'Countdown Duration',
  type: 'number',
  defaultValue: 5,
  min: 1,
  max: 300,
  guideKey: 'screenedit-basic-config-countdown-duration-value',
};

/**
 * V2 Countdown Unit
 *
 * Path: content.countdown.unit
 */
export const V2_COUNTDOWN_UNIT_CONFIG: FieldConfig = {
  path: 'content.countdown.unit',
  label: 'Countdown Unit',
  type: 'select',
  options: [...V2_COUNTDOWN_UNIT_OPTIONS],
  defaultValue: 'seconds',
  guideKey: 'screenedit-basic-config-countdown-unit',
};
